import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaUndo, FaUser, FaReceipt, FaMoneyBillWave } from 'react-icons/fa';
import { useToast } from '../../../context/ToastContext';
import PageLayout from '../../../components/ui/PageLayout';
import PageHeader from '../../../components/ui/PageHeader';
import SkeletonLoader from '../../../components/ui/SkeletonLoader';
import PortalIconPicker from '../../../components/PortalCMS/PortalIconPicker';

const PAGE_META = {
  'account-profile': {
    label: 'My Profile',
    description: 'Edit the My Profile page header, sidebar label, icon, and Help Guide card.',
    icon: FaUser,
    showNote: false,
  },
  'account-invoice': {
    label: 'Invoice Download',
    description: 'Edit the Invoice page header, notice banner, sidebar label, icon, and Help Guide card.',
    icon: FaReceipt,
    showNote: true,
  },
  'account-payment-history': {
    label: 'Payment History',
    description: 'Edit the Payment History page header, sidebar label, icon, and Help Guide card.',
    icon: FaMoneyBillWave,
    showNote: false,
  },
};

const defaultForm = () => ({
  isPublished: true,
  sidebarTitle: '',
  pageTitle: '',
  pageSubtitle: '',
  helpDescription: '',
  noteText: '',
  icon: 'FaUser',
});

const AccountPagePortalCMS = ({ slug }) => {
  const meta = PAGE_META[slug];
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState(defaultForm());

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${slug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const c = res.data.data.content || {};
        setForm({
          isPublished: res.data.data.isPublished !== false,
          sidebarTitle: c.sidebarTitle || '',
          pageTitle: c.pageTitle || '',
          pageSubtitle: c.pageSubtitle || '',
          helpDescription: c.helpDescription || '',
          noteText: c.noteText || '',
          icon: c.icon || 'FaUser',
        });
      }
    } catch {
      toastError(`Failed to load ${meta?.label || 'page'} content`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (slug) fetchContent();
  }, [slug]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${slug}`,
        { content: form, isPublished: form.isPublished },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) toastSuccess(`${meta.label} saved`);
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(`Restore preloaded defaults for ${meta.label}?`)) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${slug}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Defaults restored');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  if (!meta) return null;

  if (loading) {
    return (
      <PageLayout>
        <SkeletonLoader count={4} />
      </PageLayout>
    );
  }

  const HeaderIcon = meta.icon;

  return (
    <PageLayout>
      <PageHeader
        title={meta.label}
        description={meta.description}
        icon={HeaderIcon}
        actions={
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigate('/portal-cms/account-settings')} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 text-sm">
              Back
            </button>
            <button type="button" onClick={handleReset} disabled={resetting} className="px-4 py-2 rounded-xl border text-sm flex items-center gap-2">
              <FaUndo className="w-3.5 h-3.5" /> {resetting ? 'Resetting...' : 'Reset'}
            </button>
            <button type="button" onClick={handleSave} disabled={saving} className="btn-premium text-sm py-2 px-4 flex items-center gap-2">
              <FaSave className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      />

      <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6 space-y-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.isPublished !== false} onChange={(e) => setForm((prev) => ({ ...prev, isPublished: e.target.checked }))} />
          Published
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Sidebar label</label>
            <input className="input-modern" value={form.sidebarTitle} onChange={(e) => setForm((prev) => ({ ...prev, sidebarTitle: e.target.value }))} placeholder="Shown in sidebar dropdown" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Page header title</label>
            <input className="input-modern" value={form.pageTitle} onChange={(e) => setForm((prev) => ({ ...prev, pageTitle: e.target.value }))} placeholder="Shown at top of page" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Page subtitle</label>
            <textarea className="input-modern min-h-[80px]" value={form.pageSubtitle} onChange={(e) => setForm((prev) => ({ ...prev, pageSubtitle: e.target.value }))} placeholder="Page header subtitle" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Help Guide card text</label>
            <textarea className="input-modern min-h-[80px]" value={form.helpDescription} onChange={(e) => setForm((prev) => ({ ...prev, helpDescription: e.target.value }))} placeholder="Description on Help page card" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Icon</label>
          <PortalIconPicker value={form.icon} onChange={(icon) => setForm((prev) => ({ ...prev, icon }))} />
        </div>
        {meta.showNote && (
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Invoice notice banner</label>
            <textarea className="input-modern min-h-[90px]" value={form.noteText} onChange={(e) => setForm((prev) => ({ ...prev, noteText: e.target.value }))} placeholder="Scrolling notice on invoice page" />
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default AccountPagePortalCMS;
